import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./Content";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
); 

const TargetPriceCalculator = () => {
  const content = article.find((item) => item.id === 6);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [analysisType, setAnalysisType] = useState("profit"); // profit, return, timeframe

  const [inputs, setInputs] = useState({
    // Basic inputs
    currentPrice: "50.00",
    shares: "100",
    targetProfit: "1000",
    targetReturn: "20",
    timeframe: "365",
    
    // Advanced inputs
    commission: "10.00",
    fees: "5.00",
    dividendYield: "2.0",
    dividendFrequency: "quarterly",
    
    // Risk management
    stopLoss: "45.00",
    riskReward: "3",
    positionSize: "5000",
    
    // Technical inputs
    support: "48.00",
    resistance: "55.00",
    volatility: "15",
    
    // Common inputs
    currency: "1",
    annualReturn: "8.0"
  });

  const [targets, setTargets] = useState([
    { type: "conservative", price: "55.00", probability: "70" },
    { type: "moderate", price: "60.00", probability: "50" },
    { type: "aggressive", price: "65.00", probability: "30" }
  ]);

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateTargetPrice();
    }
  }, [inputs, targets, analysisType, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleTargetChange = (index, field, value) => {
    const updatedTargets = targets.map((target, i) => 
      i === index ? { ...target, [field]: value } : target
    );
    setTargets(updatedTargets);
  };

  const addTarget = () => {
    setTargets([...targets, { type: "custom", price: "", probability: "50" }]);
  };

  const removeTarget = (index) => {
    if (targets.length > 1) {
      setTargets(targets.filter((_, i) => i !== index));
    }
  };

  const calculateTargetPrice = () => {
    const currentPrice = parseFloat(inputs.currentPrice) || 0;
    const shares = parseFloat(inputs.shares) || 0;
    const commission = parseFloat(inputs.commission) || 0;
    const fees = parseFloat(inputs.fees) || 0;
    const currencyRate = parseFloat(inputs.currency) || 1;
    const dividendYield = parseFloat(inputs.dividendYield) || 0;
    const dividendFrequency = inputs.dividendFrequency;

    let targetPrice = 0;
    let requiredGain = 0;
    let totalReturn = 0;

    // Calculate based on analysis type
    switch (analysisType) {
      case "profit":
        const targetProfit = parseFloat(inputs.targetProfit) || 0;
        const totalCost = (currentPrice * shares) + commission + fees;
        requiredGain = targetProfit;
        targetPrice = (totalCost + targetProfit) / shares;
        totalReturn = (targetProfit / totalCost) * 100;
        break;

      case "return":
        const targetReturn = parseFloat(inputs.targetReturn) || 0;
        const investment = (currentPrice * shares) + commission + fees;
        requiredGain = (targetReturn / 100) * investment;
        targetPrice = (investment + requiredGain) / shares;
        totalReturn = targetReturn;
        break;

      case "timeframe":
        const timeframe = parseFloat(inputs.timeframe) || 365;
        const annualReturn = parseFloat(inputs.annualReturn) || 8;
        const dailyReturn = annualReturn / 36500;
        targetPrice = currentPrice * Math.pow(1 + dailyReturn, timeframe);
        requiredGain = (targetPrice - currentPrice) * shares;
        totalReturn = ((targetPrice - currentPrice) / currentPrice) * 100;
        break;
    }

    // Dividend calculations
    let dividendMultiplier = 0;
    switch(dividendFrequency) {
      case "monthly": dividendMultiplier = 12; break;
      case "quarterly": dividendMultiplier = 4; break;
      case "semi-annual": dividendMultiplier = 2; break;
      case "annual": dividendMultiplier = 1; break;
    }
    const annualDividend = (dividendYield / 100) * currentPrice * shares;
    const totalDividends = annualDividend * (parseFloat(inputs.timeframe) / 365);

    // Risk management calculations
    const stopLoss = parseFloat(inputs.stopLoss) || 0;
    const riskReward = parseFloat(inputs.riskReward) || 1;
    const potentialLoss = stopLoss ? (currentPrice - stopLoss) * shares : 0;
    const requiredReward = potentialLoss * riskReward;
    const riskAdjustedTarget = currentPrice + (requiredReward / shares);

    // Technical analysis factors
    const support = parseFloat(inputs.support) || 0;
    const resistance = parseFloat(inputs.resistance) || 0;
    const volatility = parseFloat(inputs.volatility) || 0;
    
    const technicalTarget = resistance > 0 ? resistance : currentPrice * 1.2;
    const volatilityTarget = currentPrice * (1 + (volatility / 100));

    const resultData = {
      targetPrice: targetPrice * currencyRate,
      currentPrice: currentPrice * currencyRate,
      priceDifference: (targetPrice - currentPrice) * currencyRate,
      percentGain: ((targetPrice - currentPrice) / currentPrice) * 100,
      totalReturn,
      requiredGain: requiredGain * currencyRate,
      totalInvestment: (currentPrice * shares + commission + fees) * currencyRate,
      shares,
      commission: commission * currencyRate,
      currencyRate,
      analysisType,
      
      // Dividend info
      annualDividend: annualDividend * currencyRate,
      totalDividends: totalDividends * currencyRate,
      dividendAdjustedTarget: (targetPrice - (totalDividends / shares)) * currencyRate,
      
      // Risk management
      stopLoss: stopLoss * currencyRate,
      riskReward,
      potentialLoss: potentialLoss * currencyRate,
      riskAdjustedTarget: riskAdjustedTarget * currencyRate,
      requiredReward: requiredReward * currencyRate,
      
      // Technical analysis
      support: support * currencyRate,
      resistance: resistance * currencyRate,
      technicalTarget: technicalTarget * currencyRate,
      volatilityTarget: volatilityTarget * currencyRate,
      volatility
    };

    setResult(resultData);
  };

  const calculateProbabilityAnalysis = () => {
    if (!result) return [];

    return targets.map(target => {
      const targetPrice = parseFloat(target.price) || 0;
      const probability = parseFloat(target.probability) || 0;
      const priceDifference = targetPrice - result.currentPrice;
      const percentGain = (priceDifference / result.currentPrice) * 100;
      const potentialProfit = priceDifference * result.shares;
      const expectedValue = (potentialProfit * probability) / 100;

      return {
        type: target.type,
        price: targetPrice,
        probability,
        priceDifference,
        percentGain,
        potentialProfit,
        expectedValue
      };
    });
  };

  const calculateTimeAnalysis = () => {
    if (!result) return [];

    const timeData = [];
    const months = 24;
    const annualReturn = parseFloat(inputs.annualReturn) || 8;
    const monthlyReturn = annualReturn / 1200;

    for (let i = 0; i <= months; i++) {
      const futurePrice = result.currentPrice * Math.pow(1 + monthlyReturn, i);
      const profit = (futurePrice - result.currentPrice) * result.shares;
      const isTargetReached = futurePrice >= result.targetPrice;

      timeData.push({
        month: i,
        price: futurePrice,
        profit,
        isTargetReached,
        return: ((futurePrice - result.currentPrice) / result.currentPrice) * 100
      });
    }

    return timeData;
  };

  const calculateRiskAnalysis = () => {
    if (!result) return [];

    const riskLevels = [
      { level: "Very High", return: 50, probability: 10 },
      { level: "High", return: 30, probability: 25 },
      { level: "Medium", return: 15, probability: 50 },
      { level: "Low", return: 8, probability: 75 },
      { level: "Very Low", return: 4, probability: 90 }
    ];

    return riskLevels.map(level => {
      const targetPrice = result.currentPrice * (1 + level.return / 100);
      const expectedValue = (level.return * level.probability) / 100;

      return {
        ...level,
        targetPrice,
        expectedValue,
        potentialProfit: (targetPrice - result.currentPrice) * result.shares
      };
    });
  };

  const resetCalculator = () => {
    setTargets([
      { type: "conservative", price: "55.00", probability: "70" },
      { type: "moderate", price: "60.00", probability: "50" },
      { type: "aggressive", price: "65.00", probability: "30" }
    ]);
    setInputs({
      currentPrice: "50.00",
      shares: "100",
      targetProfit: "1000",
      targetReturn: "20",
      timeframe: "365",
      commission: "10.00",
      fees: "5.00",
      dividendYield: "2.0",
      dividendFrequency: "quarterly",
      stopLoss: "45.00",
      riskReward: "3",
      positionSize: "5000",
      support: "48.00",
      resistance: "55.00",
      volatility: "15",
      currency: "1",
      annualReturn: "8.0"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Target Price,${result.targetPrice.toFixed(2)}`,
      `Current Price,${result.currentPrice.toFixed(2)}`,
      `Price Difference,${result.priceDifference.toFixed(2)}`,
      `Percent Gain,${result.percentGain.toFixed(2)}%`,
      `Total Return,${result.totalReturn.toFixed(2)}%`,
      `Required Gain,${result.requiredGain.toFixed(2)}`,
      `Total Investment,${result.totalInvestment.toFixed(2)}`,
      `Shares,${result.shares}`,
      `Analysis Type,${result.analysisType}`,
      `Risk/Reward Ratio,${result.riskReward}`,
      `Stop Loss,${result.stopLoss.toFixed(2)}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'target-price-analysis.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      targets: [...targets],
      analysisType,
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setTargets(calc.targets || []);
    setAnalysisType(calc.analysisType || "profit");
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        analysisType: "return",
        inputs: { 
          currentPrice: "100.00", 
          shares: "50", 
          targetReturn: "15",
          stopLoss: "85.00",
          riskReward: "2"
        }
      },
      moderate: { 
        analysisType: "profit",
        inputs: { 
          currentPrice: "50.00", 
          shares: "100", 
          targetProfit: "2500",
          stopLoss: "40.00",
          riskReward: "3"
        }
      },
      aggressive: { 
        analysisType: "timeframe",
        inputs: { 
          currentPrice: "25.00", 
          shares: "200", 
          timeframe: "180",
          annualReturn: "25",
          stopLoss: "20.00",
          riskReward: "4"
        }
      }
    };
    
    const presetData = presets[preset];
    setAnalysisType(presetData.analysisType);
    setInputs(prev => ({ ...prev, ...presetData.inputs }));
    addNotification(`${preset} preset applied`, "info");
  };

  // Chart Data
  const returnChartData = result ? {
    labels: ["Current Value", "Required Gain"],
    datasets: [{
      data: [result.totalInvestment, result.requiredGain],
      backgroundColor: ["#19692C", "#FFD93D"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const probabilityData = calculateProbabilityAnalysis();
  const probabilityChartData = probabilityData.length > 0 ? {
    labels: probabilityData.map(p => p.type.toUpperCase()),
    datasets: [
      {
        label: 'Probability (%)',
        data: probabilityData.map(p => p.probability),
        backgroundColor: "#19692C",
        borderColor: "#19692C",
        borderWidth: 2
      },
      {
        label: 'Expected Value',
        data: probabilityData.map(p => p.expectedValue),
        backgroundColor: "#FFD93D",
        borderColor: "#FFD93D",
        borderWidth: 2,
        type: 'line',
        yAxisID: 'y1'
      }
    ]
  } : null;

  const timeData = calculateTimeAnalysis();
  const timeChartData = timeData.length > 0 ? {
    labels: timeData.map(d => `Month ${d.month}`),
    datasets: [
      {
        label: 'Stock Price',
        data: timeData.map(d => d.price),
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: true,
        tension: 0.4
      },
      {
        label: 'Target Price',
        data: timeData.map(() => result.targetPrice),
        borderColor: "#FF6B6B",
        borderDash: [5, 5],
        fill: false,
        tension: 0.4
      }
    ]
  } : null;

  const riskData = calculateRiskAnalysis();
  const riskChartData = riskData.length > 0 ? {
    labels: riskData.map(r => r.level),
    datasets: [{
      label: 'Expected Return (%)',
      data: riskData.map(r => r.expectedValue),
      backgroundColor: riskData.map(r => 
        r.level === "Very High" ? "#E53E3E" :
        r.level === "High" ? "#ED8936" :
        r.level === "Medium" ? "#FFD93D" :
        r.level === "Low" ? "#68D391" : "#38A169"
      ),
      borderColor: riskData.map(r => 
        r.level === "Very High" ? "#E53E3E" :
        r.level === "High" ? "#ED8936" :
        r.level === "Medium" ? "#FFD93D" :
        r.level === "Low" ? "#68D391" : "#38A169"
      ),
      borderWidth: 2
    }]
  } : null;

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Target Price Calculator</h1>
        </div>
        <p>Calculate optimal target prices, analyze multiple scenarios, and optimize your profit-taking strategy</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className="profit positive">
                        ${calc.targetPrice?.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Target Price'}
              </button>
              <button 
                className={`tab ${activeTab === "scenarios" ? "active" : ""}`}
                onClick={() => setActiveTab("scenarios")}
              >
                <i className="fas fa-chart-bar"></i>
                {isMobile ? 'Scenarios' : 'Multi-Scenario'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Strategy Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Analysis Type Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${analysisType === "profit" ? "active" : ""}`}
                onClick={() => setAnalysisType("profit")}
              >
                <i className="fas fa-money-bill-wave"></i>
                Target Profit
              </button>
              <button 
                className={`mode-btn ${analysisType === "return" ? "active" : ""}`}
                onClick={() => setAnalysisType("return")}
              >
                <i className="fas fa-percentage"></i>
                Target Return %
              </button>
              <button 
                className={`mode-btn ${analysisType === "timeframe" ? "active" : ""}`}
                onClick={() => setAnalysisType("timeframe")}
              >
                <i className="fas fa-clock"></i>
                Timeframe Based
              </button>
            </div>
          </div>

          {/* Target Price Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  {/* Basic Inputs */}
                  <h3>
                    <i className="fas fa-bullseye"></i>
                    Investment Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Current Price</label>
                      <input 
                        type="number" 
                        name="currentPrice" 
                        value={inputs.currentPrice} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-layer-group"></i> Shares</label>
                      <input 
                        type="number" 
                        name="shares" 
                        value={inputs.shares} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Analysis Type Specific Inputs */}
                  {analysisType === "profit" && (
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-money-bill-wave"></i> Target Profit ($)</label>
                        <input 
                          type="number" 
                          name="targetProfit" 
                          value={inputs.targetProfit} 
                          onChange={handleChange}
                          placeholder="1000"
                          step="1"
                        />
                      </div>
                    </div>
                  )}

                  {analysisType === "return" && (
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-percentage"></i> Target Return (%)</label>
                        <input 
                          type="number" 
                          name="targetReturn" 
                          value={inputs.targetReturn} 
                          onChange={handleChange}
                          placeholder="20"
                          step="0.1"
                        />
                      </div>
                    </div>
                  )}

                  {analysisType === "timeframe" && (
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-calendar"></i> Timeframe (Days)</label>
                        <input 
                          type="number" 
                          name="timeframe" 
                          value={inputs.timeframe} 
                          onChange={handleChange}
                          placeholder="365"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-chart-line"></i> Annual Return (%)</label>
                        <input 
                          type="number" 
                          name="annualReturn" 
                          value={inputs.annualReturn} 
                          onChange={handleChange}
                          placeholder="8.0"
                          step="0.1"
                        />
                      </div>
                    </div>
                  )}

                  {/* Cost Inputs */}
                  <h3>
                    <i className="fas fa-receipt"></i>
                    Costs & Dividends
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Commission</label>
                      <input 
                        type="number" 
                        name="commission" 
                        value={inputs.commission} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Other Fees</label>
                      <input 
                        type="number" 
                        name="fees" 
                        value={inputs.fees} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill-wave"></i> Dividend Yield (%)</label>
                      <input 
                        type="number" 
                        name="dividendYield" 
                        value={inputs.dividendYield} 
                        onChange={handleChange}
                        placeholder="0.0"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Dividend Frequency</label>
                      <select name="dividendFrequency" value={inputs.dividendFrequency} onChange={handleChange}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="semi-annual">Semi-Annual</option>
                        <option value="annual">Annual</option>
                      </select>
                    </div>
                  </div>

                  {/* Risk Management */}
                  <h3>
                    <i className="fas fa-shield-alt"></i>
                    Risk Management
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Stop Loss Price</label>
                      <input 
                        type="number" 
                        name="stopLoss" 
                        value={inputs.stopLoss} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-balance-scale"></i> Risk/Reward Ratio</label>
                      <input 
                        type="number" 
                        name="riskReward" 
                        value={inputs.riskReward} 
                        onChange={handleChange}
                        placeholder="3.0"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Position Size</label>
                      <input 
                        type="number" 
                        name="positionSize" 
                        value={inputs.positionSize} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Technical Analysis */}
                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Technical Analysis
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-shield-alt"></i> Support Level</label>
                      <input 
                        type="number" 
                        name="support" 
                        value={inputs.support} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-mountain"></i> Resistance Level</label>
                      <input 
                        type="number" 
                        name="resistance" 
                        value={inputs.resistance} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-bolt"></i> Volatility (%)</label>
                      <input 
                        type="number" 
                        name="volatility" 
                        value={inputs.volatility} 
                        onChange={handleChange}
                        placeholder="15"
                        step="0.1"
                      />
                    </div>
                  </div>

                  {/* Settings */}
                  <h3>
                    <i className="fas fa-cog"></i>
                    Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-globe"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currency" 
                        value={inputs.currency} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Multi-Scenario Tab */}
          {activeTab === "scenarios" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-chess-board"></i>
                  Multiple Target Scenarios
                </h3>
                
                {targets.map((target, index) => (
                  <div key={index} className="purchase-group">
                    <br/><div className="purchase-header">
                      <h4>{target.type.toUpperCase()} Target</h4>
                      {targets.length > 1 && (
                        <button 
                          className="remove-purchase-btn"
                          onClick={() => removeTarget(index)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div><br/>
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-bullseye"></i> Target Price</label>
                        <input 
                          type="number" 
                          value={target.price}
                          onChange={(e) => handleTargetChange(index, 'price', e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-chart-pie"></i> Probability (%)</label>
                        <input 
                          type="number" 
                          value={target.probability}
                          onChange={(e) => handleTargetChange(index, 'probability', e.target.value)}
                          placeholder="50"
                          step="1"
                          min="0"
                          max="100"
                        />
                      </div>
                    </div><br/>
                  </div>
                ))}

                <button className="btn add-purchase-btn" onClick={addTarget}>
                  <i className="fas fa-plus"></i>
                  Add Another Target
                </button>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit positive">
                          <i className="fas fa-bullseye"></i>
                          ${calc.targetPrice?.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-percentage"></i> Gain: {calc.percentGain?.toFixed(2)}%</span>
                        <span><i className="fas fa-money-bill-wave"></i> Profit: ${calc.requiredGain?.toFixed(2)}</span>
                        <span><i className="fas fa-chess-board"></i> Type: {calc.analysisType}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "scenarios") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-bullseye"></i>
                    Target Price Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          ${result.targetPrice.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-bullseye"></i>
                          Target Price
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.currentPrice.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Current Price
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value positive">
                          {result.percentGain.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Required Gain
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value positive">
                          ${result.requiredGain.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Profit at Target
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-layer-group"></i> Total Shares:</span>
                        <span>{result.shares}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-money-bill-wave"></i> Total Investment:</span>
                        <span>${result.totalInvestment.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Total Commission:</span>
                        <span>${result.commission.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-exchange-alt"></i> Price Difference:</span>
                        <span>${result.priceDifference.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chess-board"></i> Analysis Type:</span>
                        <span>{result.analysisType}</span>
                      </div>
                      {result.totalDividends > 0 && (
                        <div className="detail-row">
                          <span><i className="fas fa-money-bill-wave"></i> Total Dividends:</span>
                          <span>${result.totalDividends.toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    {/* Risk Management Summary */}
                    <div className="risk-summary">
                      <h4>Risk Management</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value">
                            {result.riskReward}:1
                          </div>
                          <div className="risk-label">Risk/Reward Ratio</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value negative">
                            ${result.potentialLoss.toFixed(2)}
                          </div>
                          <div className="risk-label">Potential Loss</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.riskAdjustedTarget.toFixed(2)}
                          </div>
                          <div className="risk-label">Risk-Adjusted Target</div>
                        </div>
                      </div>
                    </div>

                    {/* Technical Analysis Summary */}
                    <div className="risk-summary">
                      <h4>Technical Analysis</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.support.toFixed(2)}
                          </div>
                          <div className="risk-label">Support Level</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.resistance.toFixed(2)}
                          </div>
                          <div className="risk-label">Resistance Level</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            {result.volatility}%
                          </div>
                          <div className="risk-label">Volatility</div>
                        </div>
                      </div>
                    </div>

                    {/* Investment Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Investment vs Required Gain
                      </h3>
                      <Doughnut 
                        data={returnChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Time Analysis Chart */}
                    {timeData.length > 0 && (
                      <div className="chart-container">
                        <h3>
                          <i className="fas fa-chart-line"></i>
                          Price Projection Timeline
                        </h3>
                        <Line 
                          data={timeChartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: true,
                            plugins: {
                              legend: {
                                display: true,
                                position: 'top'
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: false,
                                ticks: {
                                  callback: function(value) {
                                    return '$' + value.toFixed(2);
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    )}

                    {/* Risk Analysis Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Risk-Return Analysis
                      </h3>
                      <Bar 
                        data={riskChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            y: {
                              ticks: {
                                callback: function(value) {
                                  return value + '%';
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-bullseye fa-3x"></i>
                    <p>Enter your investment details to calculate target prices</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "scenarios" && result && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chess-board"></i>
                  Multi-Scenario Analysis
                </h3>
                <div className="chart-container">
                  <Bar 
                    data={probabilityChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          title: {
                            display: true,
                            text: 'Probability (%)'
                          },
                          max: 100
                        },
                        y1: {
                          position: 'right',
                          title: {
                            display: true,
                            text: 'Expected Value ($)'
                          },
                          grid: {
                            drawOnChartArea: false
                          }
                        }
                      }
                    }}
                  />
                </div>
                
                <div className="scenario-list">
                  {probabilityData.map((scenario, index) => (
                    <div key={index} className="scenario-item">
                      <div className="scenario-header">
                        <span className="scenario-name">{scenario.type.toUpperCase()}</span>
                        <span className="scenario-outcome positive">
                           ${scenario.price.toFixed(2)}
                        </span>
                      </div>
                      <div className="scenario-stats">
                        <span>Probability: {scenario.probability}%</span>
                        <span>Gain: {scenario.percentGain.toFixed(2)}%</span>
                        <span>Profit: ${scenario.potentialProfit.toFixed(2)}</span>
                        <span>Expected Value: ${scenario.expectedValue.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value">
                      ${result.targetPrice.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-bullseye"></i>
                      Primary Target
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {probabilityData.reduce((max, s) => Math.max(max, s.expectedValue), 0).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-star"></i>
                      Best Expected Value
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {probabilityData.filter(s => s.expectedValue > 0).length}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-thumbs-up"></i>
                      Positive Scenarios
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {(
                        probabilityData.reduce((sum, s) => sum + s.expectedValue, 0) / 
                        probabilityData.length
                      ).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-balance-scale"></i>
                      Avg Expected Value
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default TargetPriceCalculator;